/* global localStorage */

import Auth from '@/services/AuthService'
import store from '@/store'
import axios from 'axios'
import router from '@/router'
import config from '@/config'

const API_URL = config.apiEndpoint

const getDefaultState = () => {
  return {
    user: null,
    userData: null,
    isAuthenticated: false,
    authenticationStatus: null,
    profileHostname: null,
    profileApiKey: null
  }
}

// initial state
const state = getDefaultState()

const getters = {
  authenticatedUser: state => state.user,
  profileHostname: state => state.profileHostname,
  profileApiKey: state => state.profileApiKey,
  isAuthenticated: state => state.isAuthenticated,
  authenticationStatus: state => {
    return state.authenticationStatus
      ? state.authenticationStatus
      : { variant: 'secondary' }
  },
  hasAuthenticationStatus: state => {
    return !!state.authenticationStatus
  },
  needsNewPassword: state => {
    return (
      state.authenticationStatus &&
      state.authenticationStatus.state === 'new_password_required'
    )
  },
  hasAccessToken: state => {
    return !!state.accessToken
  },
  hasIdToken: state => {
    return !!state.idToken
  }
}

const mutations = {
  setAuthenticationError (state, err) {
    let errorMessage = err.message

    switch (err.code) {
      case 'UsernameExistsException':
        errorMessage = "L'username è stato già utilizzato da un altro utente"
        break
      case 'InvalidPasswordException':
        errorMessage = 'La password deve contenere almeno 8 caratteri'
        break
      case 'NotAuthorizedException':
        errorMessage = 'Email o password errati'
        break
      case 'UserNotFoundException':
        errorMessage = 'Utente non registrato'
        break
      case 'NetworkError':
        errorMessage = 'Errore di rete'
        break
    }

    state.authenticationStatus = {
      state: 'failed',
      message: errorMessage,
      variant: 'danger'
    }
  },
  clearAuthenticationStatus: state => {
    state.authenticationStatus = null
  },
  setNewPasswordRequiredStatus (state, authenticationStatus) {
    state.authenticationStatus = authenticationStatus
  },
  setUserAuthenticated (state, user) {
    state.user = user
    state.isAuthenticated = true
  },
  setUserData (state, userData) {
    state.userData = userData
  },
  setProfileHostname (state, hostname) {
    state.profileHostname = hostname
  },
  setProfileApiKey (state, apiKey) {
    state.profileApiKey = apiKey
  },
  clearAuthentication (state) {
    state.user = null
    state.userId = null
    state.isAuthenticated = false
  },
  resetState (state) {
    Object.assign(state, getDefaultState())
  }
}

const actions = {
  resetAuthState: context => {
    context.commit('resetState')
  },
  clearAuthentication: context => {
    context.commit('auth/clearAuthenticationStatus', null, { root: true })
  },
  clearAuthenticationStatus: context => {
    context.commit('clearAuthenticationStatus', null)
  },
  signIn: async (context, params) => {
    context.commit('auth/clearAuthenticationStatus', null, { root: true })
    try {
      const res = await Auth.signIn(
        {
          username: params.username,
          password: params.password
        },
        'sign_in'
      )

      if (res.state === 'new_password_required') {
        context.commit('setNewPasswordRequiredStatus', res)
      }

      context.commit('setUserAuthenticated', res.username)
    } catch (err) {
      context.commit('auth/setAuthenticationError', err, { root: true })
    }
  },
  newPassword: async (context, params) => {
    try {
      const res = await Auth.signIn(
        {
          username: params.username,
          password: params.currentPassword,
          newPassword: params.newPassword
        },
        'password_challenge'
      )

      context.commit('setUserAuthenticated', res.username)
      context.commit('auth/clearAuthenticationStatus', null, { root: true })
    } catch (err) {
      context.commit('auth/setAuthenticationError', err, { root: true })
    }
  },
  signOut: async context => {
    try {
      Auth.signOut()
    } catch (err) {
      console.error(err)
    }

    store.dispatch('packages/resetPackagesState')
    context.commit('resetState')

    localStorage.clear()
    context.commit('auth/clearAuthentication', null, { root: true })
  },
  passwordReset: async (context, params) => {
    context.commit('auth/clearAuthenticationStatus', null, { root: true })
    try {
      await Auth.forgotPassword(params.username)
    } catch (err) {
      context.commit('auth/setAuthenticationError', err, { root: true })
    }
  },
  confirmPasswordReset: async (context, params) => {
    context.commit('auth/clearAuthenticationStatus', null, { root: true })
    try {
      await Auth.forgotPasswordSubmit(
        params.username,
        params.code,
        params.password
      )
    } catch (err) {
      context.commit('auth/setAuthenticationError', err, { root: true })
    }
  },
  passwordChange: async (context, params) => {
    context.commit('auth/clearAuthenticationStatus', null, { root: true })
    try {
      await Auth.changePassword(params.currentPassword, params.newPassword)
    } catch (err) {
      context.commit('auth/setAuthenticationError', err, { root: true })
    }
  },
  retrieveProfileHostname: async context => {
    try {
      if (localStorage.getItem('cognito-vue-dashboard-default')) {
        const profileResource =
          JSON.parse(
            JSON.parse(localStorage.getItem('cognito-vue-dashboard-default'))
              .value
          ).auth.profileResource || null
        if (profileResource && context.state.profileResource) {
          return
        }
      }

      let jwtToken

      try {
        jwtToken = await Auth.getJwtToken()
      } catch (err) {
        await store.dispatch('auth/expiredSessionSignOut')
        return
      }

      const token = ['tk:', jwtToken].join('')

      const url = [API_URL, 'prod/profile'].join('/')

      const config = {
        headers: {
          Auth: token
        }
      }

      const response = await axios.get(url, config)
      context.commit('setProfileHostname', response.data.hostname)
    } catch (err) {
      await store.dispatch('auth/expiredSessionSignOut')
    }
  },
  expiredSessionSignOut: async context => {
    store.dispatch('auth/signOut')
    router.push({
      name: 'signIn',
      params: {
        alert: {
          title: 'Sessione scaduta', message: 'La tua sessione è scaduta, esegui nuovamente l\'accesso.'
        }
      }
    }).catch(err => console.error(err))
  },
  retrieveProfileApiKey: async context => {
    try {
      if (localStorage.getItem('cognito-vue-dashboard-default')) {
        const profileResource =
          JSON.parse(
            JSON.parse(localStorage.getItem('cognito-vue-dashboard-default'))
              .value
          ).auth.profileResource || null
        if (profileResource && context.state.profileResource) {
          return
        }
      }

      let jwtToken

      try {
        jwtToken = await Auth.getJwtToken()
      } catch (err) {
        await store.dispatch('auth/expiredSessionSignOut')
        return
      }

      const token = ['tk:', jwtToken].join('')

      const url = [API_URL, 'prod', 'profile', 'apikey'].join('/')

      const config = {
        headers: {
          Auth: token
        }
      }

      const response = await axios.get(url, config)
      context.commit('setProfileApiKey', response.data.key)
    } catch (err) {
      await store.dispatch('auth/expiredSessionSignOut')
    }
  },
  retrieveUserData: async context => {
    let jwtToken

    try {
      jwtToken = await Auth.getJwtToken()
    } catch (err) {
      await store.dispatch('auth/expiredSessionSignOut')
      return
    }

    const token = ['tk:', jwtToken].join('')

    const url = [API_URL, 'prod/admin/users', context.state.user].join('/')

    const config = {
      headers: {
        Auth: token
      }
    }

    const response = await axios.get(url, config)
    context.commit('setUserData', response.data)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
